import { useCallback, useEffect, useState } from 'react'
import { Shield, RefreshCw, Search, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'
import NewsClusters from '../components/NewsClusters'

// Dedicated cybersecurity news feed — pulls articles tagged category=cybersecurity
// by the ingester (BleepingComputer, Hacker News, Krebs, Dark Reading, etc.).
// Tech News remains for broader industry coverage.

const CYBER_SOURCES = [
  'BleepingComputer', 'The Hacker News', 'Krebs on Security', 'Dark Reading',
  'SecurityWeek', 'Threatpost', 'SANS ISC', 'CISA Advisories',
  'Sophos Naked Security', 'SentinelOne Blog', 'Unit 42', 'Mandiant',
  'CrowdStrike Blog', 'Cisco Talos', 'Microsoft MSRC', 'Securelist (Kaspersky)',
  'r/netsec',
]

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) } catch { return ts }
}

export default function CyberNewsPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [source, setSource] = useState('all')

  const fetchNews = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/news/feed', { params: { limit: 100, category: 'cybersecurity' } })
      setArticles(r.data.articles || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchNews(); const i = setInterval(fetchNews, 180_000); return () => clearInterval(i) }, [fetchNews])

  const filtered = articles.filter(a => {
    if (source !== 'all' && a.source !== source) return false
    if (searchTerm) {
      const t = searchTerm.toLowerCase()
      if (!(a.title || '').toLowerCase().includes(t) && !(a.summary || '').toLowerCase().includes(t)) return false
    }
    return true
  })

  const sourcesInView = Array.from(new Set(articles.map(a => a.source))).sort()

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Shield size={24} className="text-rose" /> Cyber Threat News
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            {articles.length} articles from {sourcesInView.length} security sources · last 7 days
          </p>
        </div>
        <button type="button" onClick={fetchNews} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Clustered view — collapses N articles about the same incident */}
      <NewsClusters days={3} limit={200} />

      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            data-kbd-search
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search titles + summaries… (press /)"
            className="input pl-8 py-1.5 text-xs"
          />
        </div>
        <select
          value={source}
          onChange={e => setSource(e.target.value)}
          aria-label="Filter by source"
          title="Source"
          className="input w-auto py-1.5 text-xs"
        >
          <option value="all">All sources ({sourcesInView.length})</option>
          {sourcesInView.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-[10px] text-text-muted ml-auto">{filtered.length} shown</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((a, i) => {
          const extra = a.extra || {}
          const enrichedCves   = (extra.cves           || []).slice(0, 4)
          const enrichedActors = (extra.actor_mentions || []).slice(0, 3)
          return (
            <a
              key={a.id || i}
              href={a.url}
              target="_blank"
              rel="noopener"
              className="card p-3 hover:bg-surface-hover transition-colors flex flex-col gap-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono text-blue-light truncate">{a.source}</span>
                <span className="text-[10px] text-text-muted whitespace-nowrap">{relativeTime(a.published_at || a.timestamp)}</span>
              </div>
              <h3 className="text-sm font-medium text-text-primary leading-snug line-clamp-3">{a.title}</h3>
              {a.summary && (
                <p className="text-[11px] text-text-secondary line-clamp-2 leading-relaxed">{a.summary}</p>
              )}
              {/* Enriched CVE + actor chips (from full-body fetch + regex) */}
              {(enrichedCves.length > 0 || enrichedActors.length > 0) && (
                <div className="flex flex-wrap gap-1 text-[9px]">
                  {enrichedCves.map((c: string) => (
                    <span key={c}
                      onClick={e => { e.preventDefault(); e.stopPropagation(); window.open(`/app/cve/${encodeURIComponent(c)}`, '_self') }}
                      className="font-mono px-1.5 py-0.5 rounded bg-rose/10 text-rose-light border border-rose/30 hover:bg-rose/20 cursor-pointer">
                      {c}
                    </span>
                  ))}
                  {enrichedActors.map((act: string) => (
                    <span key={act}
                      className="font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">
                      {act}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-auto text-[10px] text-text-muted">
                <div className="flex flex-wrap gap-1">
                  {(a.entities_topics || []).slice(0, 3).map((t: string) => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-surface-hover">{t}</span>
                  ))}
                </div>
                <ExternalLink size={9} />
              </div>
            </a>
          )
        })}
        {filtered.length === 0 && !loading && (
          <div className="col-span-full card p-6 text-center text-text-muted text-sm">
            No cyber-news articles match the filters.
          </div>
        )}
      </div>
    </div>
  )
}
